import React, { useEffect, useState } from "react";
import { ArrowUpRight, BellRing, CarFront } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../lib/supabase";

const ACTIVE_STATUSES = new Set([
  "pending",
  "accepted",
  "driver_en_route",
  "driver_arrived",
  "passengers_picked_up",
]);

export default function ActiveRideNotice() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);

  useEffect(() => {
    let active = true;
    const loadRequests = async () => {
      if (!supabase || !user?.id || !user.canTravel) {
        if (active) setRequests([]);
        return;
      }
      try {
        const { data, error } = await supabase.rpc("get_my_ride_requests");
        if (error) throw error;
        if (active) setRequests((data || []).filter((request) => ACTIVE_STATUSES.has(request.status)));
      } catch (error) {
        console.warn("Could not load active ride notices", error);
      }
    };

    loadRequests();
    const intervalId = window.setInterval(loadRequests, 15000);
    const onFocus = () => loadRequests();
    window.addEventListener("focus", onFocus);
    const channel = supabase && user?.id && user.canTravel
      ? supabase.channel(`home-active-rides-${user.id}`)
        .on("postgres_changes", {
          event: "*",
          schema: "public",
          table: "ride_requests",
          filter: `client_id=eq.${user.id}`,
        }, loadRequests)
        .subscribe()
      : null;

    return () => {
      active = false;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
      if (channel) supabase.removeChannel(channel);
    };
  }, [user?.canTravel, user?.id]);

  if (!requests.length) return null;

  return (
    <aside className="absolute left-3 right-3 top-4 z-40 max-h-[45svh] overflow-y-auto rounded-2xl border border-amber-300/70 bg-white/95 p-3 text-slate-950 shadow-[0_16px_48px_rgba(15,23,42,.28)] backdrop-blur-xl dark:border-amber-400/30 dark:bg-slate-900/95 dark:text-slate-100 sm:left-auto sm:right-6 sm:top-6 sm:w-[min(24rem,calc(100vw-3rem))] sm:p-4">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300">
          <BellRing className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-extrabold leading-5">{t("rides.active_orders", { count: requests.length })}</p>
          <p className="mt-1 text-xs leading-4 text-slate-600 dark:text-slate-300">{t("rides.active_orders_hint")}</p>
        </div>
      </div>
      <div className="mt-3 space-y-2">
        {requests.map((request) => (
          <Link
            key={request.id}
            to={`/tickets/searching?request=${encodeURIComponent(request.id)}`}
            className="flex min-h-10 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-bold transition hover:border-amber-400 hover:bg-amber-50 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-amber-400/60 dark:hover:bg-slate-700"
          >
            <CarFront className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate">
              {t(`tickets.cities.${request.from_city}`, request.from_city)} → {t(`tickets.cities.${request.to_city}`, request.to_city)}
            </span>
            <ArrowUpRight className="h-4 w-4 shrink-0 text-slate-500 dark:text-slate-400" aria-hidden="true" />
          </Link>
        ))}
      </div>
    </aside>
  );
}
